// @erp/module-cms/schemas — the STRUCTURED SHAPES shared by several content types
// (decisions.md 2026-09-13, "The Django database is the content source": A2 · A3 · A4 ·
// A5 — db-cms 0018). ONE declaration each; the content schemas (announcements / events /
// publications / gallery / categories) reuse these and never re-declare them, so a
// highlight on an event and a highlight on a publication can never drift apart, and the
// gallery's tags rule IS the rule every tags column obeys. Browser-safe: zod + plain data
// only. This file imports NOTHING from its siblings — it sits at the bottom of the
// schema graph on purpose (announcements → structured, gallery → structured; a structured
// → gallery edge would close a cycle and TDZ the barrel).
//
// Every list here is serialized to ONE nullable *_json TEXT column per locale row and
// read back with the *_json safe-degradation law (a NULL or corrupt blob reads as []).
// Each is BOUNDED (per-string caps + a per-row cap) so a runaway blob can never bloat
// the row — the events banners[] / attachments[] bounding precedent.
import { z } from "zod";
import { CategoryHue } from "@erp/core-schemas";

// ── Icon token — a heroicon NAME (Django's `icon` on highlights and categories) ────────
// It becomes a CSS class or an icon-registry lookup on the site, so it is a slug-like
// token and nothing else: lower-case letters, digits and hyphens, never markup, never a
// space. The rule is shared by Highlight.icon and cms_category.icon.
export const ICON_TOKEN_MAX = 60;
export const ICON_TOKEN_RE = /^[a-z0-9-]+$/;
export const IconToken = z
  .string()
  .trim()
  .min(1)
  .max(ICON_TOKEN_MAX)
  .regex(ICON_TOKEN_RE, "Icon must be a lower-kebab token (a-z, 0-9, hyphen)");
export type IconToken = z.infer<typeof IconToken>;

// ── The highlight colour — a categorization hue NAME (D2 / db-cms 0021, 2026-09-19) ──
// Until 0021 this was `CssHex`, a strict #rgb…#rrggbbaa grammar, because Django's
// highlight colours were free hex and "landed in a style attribute on the site". The
// stored value is now one of the nine hue names (@erp/core-schemas CategoryHue); the
// admin paints it two-tone through @erp/ui/category-hue, and a site that wants a
// literal resolves the name through the static light stops. The importer maps
// Django's hex through the same nearest-hue rule the migration used.

// ── Highlight — A3: one shape on announcements, events and publications ───────────────
/** One "Key Highlights" entry: the text, an optional icon token and an optional hue name. Order = array order (Django's ("order", created_at)). */
export const Highlight = z.object({
  text: z.string().trim().min(1).max(500),
  icon: IconToken.optional(),
  color: CategoryHue.optional(),
});
export type Highlight = z.infer<typeof Highlight>;
/** Per-row cap — Django's widest record carries 8; 30 leaves room without inviting a paste. */
export const HIGHLIGHTS_MAX = 30;
/** The bounded list a *_json column stores. */
export const Highlights = z.array(Highlight).max(HIGHLIGHTS_MAX);
export type Highlights = z.infer<typeof Highlights>;

// ── Tag — A4: bare strings, THE gallery rule (0008 tags_json), reused by every tags column ──
// Declared here and re-exported by gallery.ts under its historical names (GalleryTag /
// GALLERY_TAG_MAX / GALLERY_TAGS_MAX) — one rule, never a second declaration. Django's
// per-tag colour is dropped on purpose: the same label carried four colours there.
export const TAG_MAX = 60;
export const TAGS_MAX = 50;
export const Tag = z.string().trim().min(1).max(TAG_MAX);
export type Tag = z.infer<typeof Tag>;
/** The bounded list a tags_json column stores. */
export const Tags = z.array(Tag).max(TAGS_MAX);
export type Tags = z.infer<typeof Tags>;

// ── Contributor — A2: structured credits on a publication ──────────────────────────────
/** One credit: a person, the role they held on this issue (Django's 33 titles, Hindi kept per locale row), an optional affiliation, and an explicit sort ordinal. */
export const Contributor = z.object({
  name: z.string().trim().min(1).max(200),
  role: z.string().trim().min(1).max(100),
  affiliation: z.string().trim().max(300).optional(),
  /** Explicit display order (Django's `order`, then contribution id) — a non-negative integer, ties keep array order. */
  order: z.number().int().min(0),
});
export type Contributor = z.infer<typeof Contributor>;
/** Per-row cap — the widest Django issue carries 18 credits; 100 is the ceiling, not a target. */
export const CONTRIBUTORS_MAX = 100;
/** The bounded list contributors_json stores. */
export const Contributors = z.array(Contributor).max(CONTRIBUTORS_MAX);
export type Contributors = z.infer<typeof Contributors>;
